interface PortfolioStatsProps {
  portfolioValue: string | number;
  todayChange: string | number;
  cashBalance?: string | number;
  buyingPower?: string | number;
  isLoading?: boolean;
}

export function PortfolioStats({
  portfolioValue,
  todayChange,
  cashBalance,
  buyingPower,
  isLoading = false,
}: PortfolioStatsProps) {
  const parseNum = (value: string | number | undefined | null): number => {
    if (typeof value === "string") return parseFloat(value) || 0;
    if (typeof value === "number") return value;
    return 0;
  };

  // Calculate today's return percentage from the dollar amounts
  const portfolioNum = parseNum(portfolioValue);
  const changeNum = parseNum(todayChange);
  const yesterdayValue = portfolioNum - changeNum;
  const calculatedReturnPct = yesterdayValue !== 0 ? (changeNum / yesterdayValue) * 100 : 0;

  const formatCurrency = (value: string | number | undefined | null) => {
    const num = parseNum(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatPercent = (value: string | number | undefined | null) => {
    const num = parseNum(value);
    const sign = num >= 0 ? "+" : "";
    return `${sign}${num.toFixed(4)}%`;
  };

  const formatChange = (value: string | number | undefined | null) => {
    const num = parseNum(value);
    const sign = num >= 0 ? "+" : "";
    return `${sign}${formatCurrency(Math.abs(num))}`;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="glass-card p-4 animate-pulse">
            <div className="h-3 w-20 bg-surface-700 rounded mb-2" />
            <div className="h-7 w-28 bg-surface-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const stats: Array<{
    label: string;
    value: string;
    color: string;
    icon: React.ReactElement;
    bgColor?: string;
    subValue?: string;
  }> = [
    {
      label: "Portfolio Value",
      value: formatCurrency(portfolioValue),
      color: "text-surface-100",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      label: "Today's Change",
      value: formatChange(todayChange),
      color: parseNum(todayChange) >= 0 ? "text-green-400" : "text-red-400",
      bgColor: parseNum(todayChange) >= 0 ? "bg-green-500/10" : "bg-red-500/10",
      icon: parseNum(todayChange) >= 0 ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      ),
    },
    {
      label: "Today's Return",
      value: formatPercent(calculatedReturnPct),
      color: calculatedReturnPct >= 0 ? "text-green-400" : "text-red-400",
      bgColor: calculatedReturnPct >= 0 ? "bg-green-500/10" : "bg-red-500/10",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      label: "Cash Available",
      value: formatCurrency(cashBalance ?? 0),
      color: "text-primary-400",
      bgColor: "bg-primary-500/10",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: "Buying Power",
      value: formatCurrency(buyingPower ?? 0),
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-5">
      {stats.map((stat, i) => (
        <div
          key={i}
          className={`glass-card p-3 sm:p-5 transition-all hover:border-surface-600/50 overflow-hidden ${
            stat.bgColor ? stat.bgColor : ""
          }`}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 min-w-0">
            <span className={`shrink-0 ${stat.color}`}>{stat.icon}</span>
            <span className="text-surface-400 text-xs sm:text-sm truncate">{stat.label}</span>
          </div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className={`text-base sm:text-2xl font-bold ${stat.color} truncate`}>{stat.value}</span>
            {stat.subValue && (
              <span className={`text-sm ${stat.color} shrink-0`}>{stat.subValue}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
